import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Phone, ArrowRight, Shield, Loader2, CheckCircle,
  Download, Activity, ShoppingBag, ArrowLeft, Clock
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND = { primary: "#1e3a6e", secondary: "#2952a3" };

export default function CustomerPortalPage() {
  const [customerToken, setCustomerToken] = useState(localStorage.getItem("hearclear_customer_token"));
  const [customer, setCustomer] = useState(null);
  const [tests, setTests] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);

  // OTP state
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  // View state
  const [selectedTest, setSelectedTest] = useState(null);

  useEffect(() => {
    if (customerToken) {
      fetchTests();
      fetchRecommendations();
    }
  }, [customerToken]);

  const requestOtp = async () => {
    if (!phone.trim() || phone.replace(/\D/g, "").length < 10) {
      setAuthError("Enter a valid 10-digit phone number");
      return;
    }
    setOtpLoading(true);
    setAuthError("");
    try {
      await axios.post(`${API}/customer/request-otp?phone=${phone}`);
      setOtpSent(true);
    } catch (err) {
      setAuthError(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    setVerifyLoading(true);
    setAuthError("");
    try {
      const res = await axios.post(`${API}/customer/verify-otp?phone=${phone}&otp=${otp}`);
      localStorage.setItem("hearclear_customer_token", res.data.token);
      setCustomerToken(res.data.token);
      setCustomer(res.data.patient);
    } catch (err) {
      setAuthError(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setVerifyLoading(false);
    }
  };

  const fetchTests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("hearclear_customer_token");
      const res = await axios.get(`${API}/customer/tests`, { headers: { Authorization: `Bearer ${token}` } });
      setTests(res.data.tests || []);
    } catch (err) {
      if (err.response?.status === 401) { logout(); }
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const token = localStorage.getItem("hearclear_customer_token");
      const res = await axios.get(`${API}/customer/recommendations`, { headers: { Authorization: `Bearer ${token}` } });
      setRecommendations(res.data);
    } catch {}
  };

  const downloadReport = async (sessionId) => {
    try {
      const token = localStorage.getItem("hearclear_customer_token");
      const res = await axios.get(`${API}/customer/tests/${sessionId}/report`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `HearClear_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Could not download report. " + (err.response?.data?.detail || ""));
    }
  };

  const logout = () => {
    localStorage.removeItem("hearclear_customer_token");
    setCustomerToken(null);
    setCustomer(null);
    setTests([]);
    setOtpSent(false);
    setOtp("");
  };

  const getSeverityColor = (s) => {
    const m = { Normal: "bg-green-100 text-green-700", Mild: "bg-yellow-100 text-yellow-700", Moderate: "bg-orange-100 text-orange-700", "Moderately Severe": "bg-red-100 text-red-700", Severe: "bg-red-200 text-red-800", Profound: "bg-red-300 text-red-900" };
    return m[s] || "bg-gray-100 text-gray-600";
  };

  // --- LOGIN SCREEN ---
  if (!customerToken) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <a href="/" className="inline-flex items-center gap-2 mb-4">
              <img src="/images/hearclear_logo.png" alt="HearClear" className="h-12 w-auto" />
            </a>
            <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="portal-heading">My Hearing Tests</h1>
            <p className="text-gray-600 text-sm">Login with your phone number to view your test results</p>
          </div>

          <Card className="shadow-xl">
            <CardContent className="p-6">
              {!otpSent ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Phone Number</Label>
                    <div className="flex mt-1.5">
                      <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-200 rounded-l-md text-sm text-gray-500">+91</div>
                      <Input data-testid="login-phone" placeholder="98765 43210" value={phone}
                        onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        className="h-11 rounded-l-none" />
                    </div>
                  </div>
                  {authError && <p className="text-sm text-red-500">{authError}</p>}
                  <Button data-testid="send-otp-btn" onClick={requestOtp} disabled={otpLoading} className="w-full h-11 text-white" style={{ background: BRAND.primary }}>
                    {otpLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
                    Send OTP
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">OTP sent to +91-{phone}. <button onClick={() => setOtpSent(false)} className="text-blue-700 underline">Change</button></p>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Enter OTP</Label>
                    <Input data-testid="login-otp" placeholder="Enter 4-digit OTP" value={otp} maxLength={4}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))} className="h-11 mt-1.5 text-center text-lg tracking-widest" />
                  </div>
                  {authError && <p className="text-sm text-red-500">{authError}</p>}
                  <Button data-testid="verify-otp-btn" onClick={verifyOtp} disabled={verifyLoading || otp.length < 4} className="w-full h-11 text-white" style={{ background: BRAND.primary }}>
                    {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                    Verify & Login
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="text-center mt-6">
            <a href="/" className="text-sm text-teal-600 hover:text-teal-700 inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Hearing Test
            </a>
          </div>
        </div>
      </div>
    );
  }

  // --- MAIN PORTAL ---
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="/images/hearclear_logo.png" alt="HearClear" className="h-8 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 hidden sm:block">{customer?.name || customer?.phone}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="text-gray-500 text-xs" data-testid="logout-btn">Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-xl font-bold text-gray-900 mb-6" data-testid="portal-title">My Hearing Tests</h1>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
        ) : tests.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No tests found. Take your first hearing test!</p>
              <Button asChild className="text-white" style={{ background: BRAND.primary }}><a href="/">Take Hearing Test</a></Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {tests.map(test => (
              <Card key={test.id} className="hover:shadow-md transition-shadow" data-testid={`test-card-${test.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={test.status === "completed" ? "default" : "secondary"}
                          className={test.status === "completed" ? "bg-green-100 text-green-700" : ""}>
                          {test.status === "completed" ? "Completed" : test.status?.replace(/_/g, " ")}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {test.created_at ? new Date(test.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </span>
                      </div>
                      {test.status === "completed" && (
                        <div className="flex flex-wrap gap-4 mt-2">
                          <div>
                            <span className="text-xs text-gray-500">Right Ear</span>
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-gray-900">{test.pta_right ?? "N/A"} dB</span>
                              {test.severity_right && <Badge className={`text-xs ${getSeverityColor(test.severity_right)}`}>{test.severity_right}</Badge>}
                            </div>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">Left Ear</span>
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-gray-900">{test.pta_left ?? "N/A"} dB</span>
                              {test.severity_left && <Badge className={`text-xs ${getSeverityColor(test.severity_left)}`}>{test.severity_left}</Badge>}
                            </div>
                          </div>
                          {test.reliability_score != null && (
                            <div>
                              <span className="text-xs text-gray-500">Reliability</span>
                              <p className="font-bold text-gray-900">{Math.round(test.reliability_score * 100)}%</p>
                            </div>
                          )}
                        </div>
                      )}
                      {test.ai_recommendation && (
                        <p className="text-sm text-gray-600 mt-3 line-clamp-2">{test.ai_recommendation.slice(0, 200)}...</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {test.status === "completed" && (
                        <Button size="sm" variant="outline" onClick={() => downloadReport(test.id)} data-testid={`download-report-${test.id}`}>
                          <Download className="w-4 h-4 mr-1" /> Report
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Recommendations */}
        {recommendations?.recommendations?.length > 0 && (
          <div className="mt-10">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Recommended Hearing Aids</h2>
            {recommendations.test_results?.ai_recommendation && (
              <Card className="mb-4 bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <p className="text-sm text-blue-900">{recommendations.test_results.ai_recommendation.slice(0, 500)}</p>
                </CardContent>
              </Card>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommendations.recommendations.map(aid => (
                <Card key={aid.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">{aid.name}</h3>
                    <p className="text-xs text-gray-500 mb-2">{aid.brand} - {aid.form_factor}</p>
                    <p className="font-bold text-teal-700 mb-3">&#8377;{(aid.price || 0).toLocaleString("en-IN")}</p>
                    <Button asChild size="sm" className="w-full text-white" style={{ background: BRAND.primary }} data-testid={`shop-${aid.id}`}>
                      <a href={aid.shop_url} target="_blank" rel="noreferrer">
                        <ShoppingBag className="w-3.5 h-3.5 mr-1" /> View on HearClear Shop
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Take Another Test CTA */}
        <div className="mt-10 text-center">
          <Button asChild size="lg" className="text-white rounded-full px-8" style={{ background: BRAND.primary }}>
            <a href="/" data-testid="take-another-test">Take Another Test <ArrowRight className="w-4 h-4 ml-2" /></a>
          </Button>
        </div>
      </div>
    </div>
  );
}
